const SEVERITY_ORDER = { high: 0, medium: 1, low: 2, info: 3 };
/** 文面に載せる代表の所見。重要度が最も高いものを選び、同じ重要度なら先頭を採る */
export const pickPrimaryFinding = (findings) => {
    let best = null;
    for (const finding of findings ?? []) {
        const rank = SEVERITY_ORDER[finding.severity] ?? 9;
        if (!best || rank < (SEVERITY_ORDER[best.severity] ?? 9))
            best = finding;
    }
    return best;
};
/**
 * 推奨対応の定型文。所見に recommendation が無い場合に、題名（次いで補足）の語句から選ぶ。
 * 上から順に評価するため、より具体的な条件を先に置く。
 */
const RECOMMENDATION_RULES = [
    {
        pattern: /EOL|サポート終了|サポートが終了|end[- ]of[- ]life/i,
        text: 'サポートが継続しているバージョンへの更新をご検討ください。すぐの更新が難しい場合は、影響範囲の確認と WAF 等による緩和策をお勧めします。',
    },
    {
        pattern: /既知(の)?脆弱性|CVE-\d|脆弱性が未修正|古いバージョン|旧バージョン|バージョンが古い/i,
        text: '該当ソフトウェアを修正済みのバージョンへ更新し、公開されている脆弱性情報との照合をお勧めします。',
    },
    {
        pattern: /バージョン(情報)?(が|の)?(露出|開示|表示|公開)|X-Powered-By|Server ?ヘッダ|version disclosure/i,
        text: 'Server / X-Powered-By 等のヘッダーからバージョン情報を出さない設定にし、あわせて最新版への更新をご検討ください。',
    },
    {
        pattern: /HSTS|Strict-Transport-Security/i,
        text: 'Strict-Transport-Security ヘッダーを設定し、HTTPS での接続を常時強制することをお勧めします。',
    },
    {
        pattern: /Content-Security-Policy|\bCSP\b/i,
        text: 'Content-Security-Policy ヘッダーの導入をご検討ください。まず report-only で影響を確認してから適用するのが安全です。',
    },
    {
        pattern: /X-Frame-Options|frame-ancestors|クリックジャッキング/i,
        text: 'X-Frame-Options または CSP の frame-ancestors で、他サイトからの埋め込みを制限することをお勧めします。',
    },
    {
        pattern: /X-Content-Type-Options|nosniff|MIME/i,
        text: 'X-Content-Type-Options: nosniff を設定し、ブラウザによる MIME タイプの推測を無効にすることをお勧めします。',
    },
    {
        pattern: /Cookie|HttpOnly|Secure ?属性|SameSite/i,
        text: 'セッション Cookie に Secure・HttpOnly・SameSite 属性を付与することをお勧めします。',
    },
    {
        pattern: /TLS|SSL|証明書|暗号スイート|cipher/i,
        text: 'TLS 1.2 以上のみを有効にし、証明書の有効期限と暗号スイートの設定を見直すことをお勧めします。',
    },
    {
        pattern: /Referrer-Policy|Permissions-Policy|セキュリティヘッダ/i,
        text: '不足しているセキュリティヘッダーの設定をご検討ください。',
    },
    {
        pattern: /HTTPS|平文|http:\/\//i,
        text: 'HTTPS への常時リダイレクトを設定し、平文での通信を無くすことをお勧めします。',
    },
    {
        pattern: /ディレクトリ(リスティング|一覧)|directory listing|index of/i,
        text: 'ディレクトリ一覧の表示を無効化し、公開が不要なファイルを取り除くことをお勧めします。',
    },
    {
        pattern: /ログイン|認証|管理画面|\badmin\b/i,
        text: 'ログイン画面や管理画面へのアクセス制限（IP 制限・多要素認証）と、試行回数の制限をご検討ください。',
    },
    {
        pattern: /WordPress|プラグイン|\bCMS\b/i,
        text: 'CMS 本体・プラグイン・テーマを最新版に更新し、使っていないものは削除することをお勧めします。',
    },
];
const GENERIC_RECOMMENDATION = '該当する設定やソフトウェアの状態をご確認のうえ、最新版への更新や推奨設定の適用をご検討ください。';
/**
 * 所見に対する推奨対応。
 * AI が出した recommendation があればそれを使い、無ければ定型文で補う（旧データ向け）。
 */
export const recommendationFor = (finding) => {
    const own = finding.recommendation?.trim();
    if (own)
        return own;
    // 題名（と分類）を優先し、補足は題名で決められないときだけ見る
    const haystacks = [`${finding.category ?? ''} ${finding.title}`, finding.note ?? ''];
    for (const haystack of haystacks) {
        if (!haystack.trim())
            continue;
        const rule = RECOMMENDATION_RULES.find((r) => r.pattern.test(haystack));
        if (rule)
            return rule.text;
    }
    return GENERIC_RECOMMENDATION;
};
// 大文字の EC を単語として探す（HRTech / EdTech の「ec」に反応しないよう大文字小文字を区別する）
const EC_INDUSTRY = /(^|[^A-Za-z])EC([^A-Za-z]|$)|D2C|通販|ネットショップ|オンラインショップ|小売|物販|ショッピング/;
const isEc = (ctx) => EC_INDUSTRY.test(ctx.industry ?? '');
const PITCH_VERSION = '今回のようなバージョンの指摘についても、「古い」という事実だけで終えるのではなく、実際にそのバージョンで悪用可能な弱点が残っているのか、外部から到達できる経路があるのかまで確認することが可能です。';
const PITCH_LOGIN = 'ログイン周りについても、設定の有無だけでなく、実際にログイン情報が保護されているか、総当たりや認証回避の経路が無いかまで確認することが可能です。';
const PITCH_TRANSPORT = '通信の暗号化についても、設定の指摘にとどまらず、実際に盗聴や改ざんにつながる経路があるかまで確認することが可能です。';
const PITCH_GENERIC = '今回の指摘事項についても、設定の指摘にとどまらず、実際に悪用可能な経路があるかまで確認することが可能です。';
/** 題名から「製品名 バージョン」を取り出す（例: "PHP 7.4.33: サポート終了…" → "PHP 7.4.33"） */
const productFromTitle = (title) => {
    const m = title.match(/^([A-Za-z][A-Za-z0-9 .+_-]*?\s\d[\w.+-]*)/);
    return m ? m[1].trim() : null;
};
const numberFromTitle = (title, pattern) => {
    const m = title.match(pattern);
    return m ? m[1] : null;
};
/** 空の段落を落とす */
const paragraphs = (...items) => items.filter((p) => typeof p === 'string' && p.trim().length > 0);
/** 上から順に評価するため、より具体的な条件を先に置く */
const EXPLANATION_RULES = [
    {
        // 改ざん事案のあった配信元（polyfill.io 等）からの読み込み
        pattern: /改ざん事案のあった配信元/,
        build: (finding) => ({
            heading: '改ざんされた配信元からのプログラムの読み込み',
            observed: '貴社Webサイトが、過去に改ざんされ、閲覧者に不正なプログラムを配信したことが報告されている外部の配信元から、プログラム（JavaScript）を読み込んでいることが確認されました。',
            why: paragraphs('外部から読み込むプログラムは、貴社のページの一部として閲覧者のブラウザで動きます。配信元が悪意のある第三者の管理下にあると、閲覧者が不正なサイトへ誘導されるなどの被害につながるおそれがあります。', finding.evidence ? `確認した読み込み先：${finding.evidence}` : null),
            action: '対策として、この読み込みを速やかに削除し、必要な機能は信頼できる配信元または自社のサーバーから配信することを推奨いたします。',
            pitch: PITCH_GENERIC,
        }),
    },
    {
        // JS ライブラリの既知の脆弱性（Retire.js との照合）
        pattern: /^library\b.*既知の脆弱性の対象範囲/,
        build: (finding, ctx) => {
            const product = productFromTitle(finding.title);
            const kinds = /脆弱性（([^）]+)）の対象範囲/.exec(finding.note ?? '')?.[1];
            return {
                heading: '古いバージョンの JavaScript ライブラリ',
                observed: `貴社Webサイトで使われている JavaScript のライブラリ${product ? `（${product}）` : ''}が、公開されている脆弱性${kinds ? `（${kinds}）` : ''}の対象となるバージョンであることが確認されました。`,
                why: paragraphs('ライブラリはページの表示や入力の処理に使われる部品で、古いバージョンには、その後に修正された弱点が残っていることがあります。', '実際に影響があるかはサイトでの使い方によりますが、公開されている情報をもとに狙われやすい状態です。', isEc(ctx) &&
                    '貴社のようにオンラインで商品を取り扱うWebサイトでは、入力画面や購入画面で使われていることが多く、特に確認をおすすめします。'),
                action: `対策として、${recommendationFor(finding).replace(/^対策として、?/, '').replace(/をご検討ください。$/, 'を推奨いたします。')}`,
                pitch: PITCH_VERSION,
            };
        },
    },
    {
        // 証明書の期限切れ（「近づいている」ではなく、すでに切れている）
        pattern: /証明書の有効期限切れ|証明書.*期限.*切れ/i,
        build: (finding, ctx) => {
            const days = numberFromTitle(finding.title, /(\d+)\s*日前/);
            return {
                heading: 'TLS証明書の有効期限切れ',
                observed: `貴社WebサイトのTLS（SSL）証明書の有効期限が${days ? `${days}日前に` : 'すでに'}切れている状態が確認されました。`,
                why: paragraphs('証明書の期限が切れていると、閲覧者のブラウザに「安全ではない」といった警告が表示され、サイトにアクセスできない、あるいは信頼を損なうといった影響が生じます。', isEc(ctx)
                    ? '貴社のようにオンラインで商品を取り扱うWebサイトでは、購入手続きが止まり、売上や問い合わせに直接影響します。'
                    : 'お問い合わせフォームなど、サイト経由の業務が止まることもあります。'),
                action: '対策として、速やかに証明書を更新し、あわせて自動更新の仕組みを導入して更新漏れを防ぐことを推奨いたします。',
                pitch: '証明書の管理についても、期限の確認だけでなく、暗号化方式の設定を含めて「実際に安全な通信になっているか」を確認することが可能です。',
            };
        },
    },
    {
        pattern: /証明書のホスト名|自己署名/i,
        build: (finding, ctx) => ({
            heading: 'TLS証明書の設定に関する確認事項',
            observed: /自己署名/.test(finding.title)
                ? '貴社Webサイトで、公的な認証局による署名でない証明書（自己署名の疑い）が使われている状態が確認されました。'
                : '貴社Webサイトの証明書に記載されたホスト名が、接続先のドメインと一致しない状態が確認されました。',
            why: paragraphs('この状態では、閲覧者のブラウザに警告が表示され、サイトの信頼性や利用の継続に影響する可能性があります。', isEc(ctx) && '貴社のようにオンラインで商品を取り扱うWebサイトでは、警告表示が購入手続きの離脱につながりやすい点です。'),
            action: '対策として、接続先ドメインに対応した、公的な認証局が発行する証明書への設定の見直しを推奨いたします。',
            pitch: '証明書まわりについても、設定の指摘にとどまらず、実際に安全な通信が確立できているかまで確認することが可能です。',
        }),
    },
    {
        pattern: /DMARC/i,
        build: (finding, ctx) => {
            const isNone = /none/.test(finding.title);
            return {
                heading: isNone ? 'なりすましメール対策（DMARC）の適用範囲' : 'なりすましメール対策（DMARC）の設定',
                observed: isNone
                    ? '貴社ドメインのDMARC（メールのなりすまし対策）は設定されているものの、ポリシーが「監視のみ（none）」で、なりすましメールを受信側で拒否しない設定であることが確認されました。'
                    : '貴社ドメインについて、メールのなりすましを検知・拒否するDMARCレコードが確認できませんでした。',
                why: paragraphs('設定が十分でない場合、第三者が貴社の会社名やドメインを装ったメールを送りやすい状態になり、取引先やお客様が偽メールを受け取るおそれがあります。', isEc(ctx)
                    ? '貴社のようにお客様や取引先と日常的にメールをやり取りする事業では、なりすましメールによる被害や信頼低下の影響が大きくなりやすい点です。'
                    : '取引先とのやり取りを装ったメールは、請求や連絡の偽装に悪用される可能性があります。'),
                action: isNone
                    ? '対策として、運用状況を確認のうえ、DMARCのポリシーを段階的に quarantine・reject へ引き上げることを推奨いたします。'
                    : '対策として、まず監視（p=none）からDMARCの設定を始め、SPF・DKIMとあわせて段階的に強化することを推奨いたします。',
                pitch: 'メールのなりすまし対策についても、レコードの有無だけでなく、実際に送信ドメイン認証が正しく機能しているかまで確認することが可能です。',
            };
        },
    },
    {
        pattern: /SPF レコード未設定|SPF レコードが/i,
        build: (_finding, ctx) => ({
            heading: 'なりすましメール対策（SPF）の設定',
            observed: '貴社ドメインについて、送信元サーバーを制限するSPFレコードが確認できませんでした。',
            why: paragraphs('SPFは、なりすましメール対策の基本的な仕組みの一つです。設定が無い場合、第三者が貴社ドメインを装ったメールを送りやすい状態になります。', isEc(ctx) && '貴社のようにお客様とメールでやり取りする事業では、なりすましによる被害や信頼低下につながりやすい点です。'),
            action: '対策として、SPFレコードを設定し、あわせてDKIM・DMARCも含めた送信ドメイン認証の整備を推奨いたします。',
            pitch: 'メールのなりすまし対策についても、設定の有無だけでなく、実際に認証が機能しているかまで確認することが可能です。',
        }),
    },
    {
        pattern: /使用プラットフォームの推定/i,
        build: (finding, ctx) => {
            const name = finding.evidence || finding.title.replace(/.*推定:\s*/, '');
            return {
                heading: '使用プラットフォームに応じた点検',
                observed: `貴社Webサイトについて、公開情報から${name ? `「${name}」の` : ''}利用が推定されました。`,
                why: paragraphs('広く使われているプラットフォームは便利な一方、公開されている設定情報や既知の注意点を踏まえた点検が有効です。', isEc(ctx) && '貴社のようにオンラインで商品を扱うサイトでは、決済や会員機能まわりの設定確認が特に重要です。'),
                action: `対策として、${name ? `${name}の` : 'ご利用中のプラットフォームの'}推奨設定・更新方針に沿った点検を推奨いたします。`,
                pitch: `${name ? `${name}の` : 'ご利用のプラットフォームの'}特性を踏まえ、設定や公開範囲に悪用可能な経路がないかまで確認することが可能です。`,
            };
        },
    },
    {
        pattern: /ログイン(画面|フォーム).*(http:\/\/|暗号化されていない|暗号化なし)/i,
        build: (_finding, ctx) => ({
            heading: '暗号化されていないログイン画面',
            observed: 'ログイン画面、またはログイン情報の送信先が、暗号化されていない http:// で提供されている状態が確認されました。',
            why: paragraphs('この状態では、IDやパスワードが暗号化されずに送信されるため、通信経路上で第三者に盗み見られる可能性があります。', isEc(ctx) &&
                '貴社のようにオンラインで商品を取り扱うWebサイトでは、会員ログインや購入手続きでお客様の情報を扱うため、ログイン周りの通信の保護は特に重要です。'),
            action: '対策として、ログイン画面とその送信先をHTTPS（暗号化通信）で提供し、http:// でのアクセスはHTTPSへ転送する構成に改めることを推奨いたします。',
            pitch: PITCH_LOGIN,
        }),
    },
    {
        pattern: /HSTS|Strict-Transport-Security/i,
        build: (_finding, ctx) => ({
            heading: 'ログイン画面のHTTPS強制（HSTS）設定',
            observed: 'ログイン画面において、常にHTTPS（暗号化通信）での接続を強制する設定（HSTS：Strict-Transport-Security）が確認されませんでした。',
            why: paragraphs('この設定が無い場合、利用者が誤って http:// でアクセスしたときや、通信経路に割り込まれたときに、ログイン情報が暗号化されない経路を通る可能性があります。', isEc(ctx)
                ? '貴社のようにオンラインで商品を取り扱うWebサイトでは、会員ログインや購入手続きでお客様の情報を扱うため、ログイン周りの通信が常に保護されていることは特に重要です。'
                : 'ログイン情報は、その後の操作すべての入口になるため、通信が常に保護されていることが重要です。'),
            action: '対策として、Strict-Transport-Security ヘッダーを設定し、HTTPSでの接続を常時強制することを推奨いたします。',
            pitch: PITCH_LOGIN,
        }),
    },
    {
        pattern: /Cookie|HttpOnly|Secure ?属性|SameSite/i,
        build: (_finding, ctx) => ({
            heading: 'Cookieのセキュリティ属性に関する設定',
            observed: 'Webサイトから発行される一部のCookieについて、Secure属性またはHttpOnly属性が付与されていない状態が確認されました。',
            why: paragraphs(`Cookieは、Webサイトにおけるユーザーの状態管理やセッション維持などに利用される仕組みです。${isEc(ctx)
                ? '貴社のようにオンラインで商品を取り扱うWebサイトでは、購入手続きや会員向け機能など、お客様の操作状態を継続して扱う場面が想定されます。'
                : 'ログインやお問い合わせなど、利用者の操作状態を継続して扱う場面で使われます。'}`, 'そのため、Cookieの保護設定が十分でない場合、サイト全体の構成やCookieの用途、その他のセキュリティ対策との組み合わせによっては、第三者によるCookieの取得やセッションの不正利用につながる可能性があります。', isEc(ctx) &&
                '特にECサイトでは、Cookie単体の設定だけでなく、ログインから商品選択、カート、購入手続きに至る一連の導線において、セッションが適切に保護されているかを確認することが重要です。'),
            action: '対策として、セッション管理に使用するCookieを特定したうえで、用途に応じてSecure・HttpOnly・SameSite属性を適切に設定し、あわせてセッションIDの発行・更新・失効などの管理方法についてもご確認いただくことを推奨いたします。',
            pitch: '今回のようなCookie設定についても、設定上の指摘だけで終えるのではなく、実際のログインや購入などの導線を踏まえ、「セッションの乗っ取りや不正利用につながる経路が存在するのか」「他の設定や挙動と組み合わせた場合に悪用可能なのか」といった観点まで確認することが可能です。',
        }),
    },
    {
        pattern: /混在コンテンツ|mixed content/i,
        build: (finding, ctx) => {
            const count = numberFromTitle(finding.title, /(\d+)\s*件/);
            return {
                heading: '暗号化されていない通信の混在（混在コンテンツ）',
                observed: `HTTPS（暗号化通信）で提供されているページの中に、暗号化されていない http:// で読み込まれる画像やスクリプトなどの要素が${count ? `${count}件` : ''}含まれている状態が確認されました。`,
                why: paragraphs('暗号化されていない要素は、通信経路上で内容を書き換えられたり、盗み見られたりする余地があります。', 'また、ブラウザによっては警告が表示されたり、要素の読み込みが遮断されたりするため、閲覧者の信頼や表示の正常性にも影響します。', isEc(ctx) &&
                    '貴社のようにオンラインで商品を取り扱うWebサイトでは、購入手続きの途中で警告が出ると離脱につながりやすく、売上面でも影響が出やすい点です。'),
                action: '対策として、ページ内で参照している画像・スクリプト・スタイルシートなどの読み込み先をすべて https:// に統一し、http:// への参照が残っていないかをご確認いただくことを推奨いたします。',
                pitch: '今回のような混在コンテンツについても、指摘にとどまらず、実際に通信の書き換えやスクリプトの差し込みにつながる経路があるかまで確認することが可能です。',
            };
        },
    },
    {
        pattern: /https.*http.*(ダウングレード|転送|リダイレクト)|リダイレクトダウングレード/i,
        build: (_finding, ctx) => ({
            heading: 'HTTPSからHTTPへの転送（暗号化の解除）',
            observed: '貴社Webサイトにおいて、HTTPS（暗号化通信）でアクセスした際に、暗号化されていない http:// のページへ転送される動作が確認されました。',
            why: paragraphs('暗号化が解除された状態では、閲覧内容や入力内容が通信経路上で盗み見られたり、書き換えられたりする余地が生じます。', 'また、ブラウザの警告表示により、閲覧者の信頼にも影響します。', isEc(ctx) &&
                '貴社のようにオンラインで商品を取り扱うWebサイトでは、お客様の入力内容を扱う場面が多いため、サイト全体が暗号化されていることが特に重要です。'),
            action: '対策として、サイト全体をHTTPSで提供し、http:// へのアクセスは https:// へ転送する構成に改めることを推奨いたします。',
            pitch: PITCH_TRANSPORT,
        }),
    },
    {
        pattern: /証明書.*残り\s*\d+\s*日|証明書.*期限|certificate.*expir/i,
        build: (finding, ctx) => {
            const days = numberFromTitle(finding.title, /残り\s*(\d+)\s*日/);
            return {
                heading: 'TLS証明書の有効期限',
                observed: `貴社WebサイトのTLS（SSL）証明書の有効期限が近づいており、${days ? `残り${days}日で` : '間もなく'}期限を迎える状態が確認されました。`,
                why: paragraphs('証明書の期限が切れると、閲覧者のブラウザに「安全ではない」といった警告が表示され、サイトにアクセスできない、あるいは信頼を損なうといった影響が生じます。', isEc(ctx)
                    ? '貴社のようにオンラインで商品を取り扱うWebサイトでは、購入手続きが止まり、売上や問い合わせに直接影響します。'
                    : 'お問い合わせフォームなど、サイト経由の業務が止まることもあります。'),
                action: '対策として、期限前に証明書を更新し、可能であれば自動更新の仕組みを導入して、更新漏れを防ぐことを推奨いたします。',
                pitch: '証明書の管理についても、期限の確認だけでなく、暗号化方式の設定を含めて「実際に安全な通信になっているか」を確認することが可能です。',
            };
        },
    },
    {
        pattern: /EOL|サポート終了|サポートが終了|end[- ]of[- ]life/i,
        build: (finding, ctx) => {
            const product = productFromTitle(finding.title);
            const hasCve = /CVE-\d/i.test(finding.note ?? '');
            return {
                heading: 'サポートが終了したソフトウェアの使用',
                observed: `貴社Webサイトで使用されているソフトウェア${product ? `（${product}）` : ''}が、提供元によるサポートが終了したバージョンであることが確認されました。`,
                why: paragraphs(`サポート終了後に見つかった弱点は修正が提供されないため、公開されている情報をもとに攻撃を受けやすい状態が続く可能性があります。${hasCve ? 'このバージョンは、公開されている脆弱性情報の対象に含まれる可能性があります（応答の表記のみに基づく確認のため、実際には修正済みの場合もあります）。' : ''}`, isEc(ctx) &&
                    '貴社のようにオンラインで商品を取り扱うWebサイトでは、お客様情報や決済に関わる情報を扱うため、基盤となるソフトウェアの状態は特に重要です。'),
                action: '対策として、サポートが継続しているバージョンへの更新を推奨いたします。すぐの更新が難しい場合は、影響範囲の確認と、WAF等による緩和策をご検討ください。',
                pitch: PITCH_VERSION,
            };
        },
    },
    {
        pattern: /古いバージョン|旧バージョン|バージョンが古い|既知(の)?脆弱性|CVE-\d|修正が含まれていない/i,
        build: (finding, ctx) => {
            const product = productFromTitle(finding.title);
            return {
                heading: 'ソフトウェアのバージョンが古い状態',
                observed: `貴社Webサイトで使用されているソフトウェア${product ? `（${product}）` : ''}が、最新版より古いバージョンであることが確認されました。`,
                why: paragraphs('古いバージョンには、その後に修正された弱点が含まれている可能性があります。', 'サポート自体は続いていても、修正を適用していない期間が長いほど、公開されている情報をもとに狙われやすくなります。', isEc(ctx) &&
                    '貴社のようにオンラインで商品を取り扱うWebサイトでは、お客様情報や決済に関わる情報を扱うため、基盤となるソフトウェアの状態は特に重要です。'),
                action: '対策として、修正済みの最新バージョンへ更新し、公開されている脆弱性情報との照合を行うことを推奨いたします。',
                pitch: PITCH_VERSION,
            };
        },
    },
    {
        pattern: /バージョン(情報)?(を|が|の)?(公開|露出|開示|表示)|X-Powered-By|Server ?ヘッダ|version disclosure/i,
        build: (finding) => ({
            heading: 'サーバーソフトウェアのバージョン情報の公開',
            observed: `Webサーバーからの応答に、使用しているソフトウェアの名称とバージョン${finding.evidence ? `（${finding.evidence}）` : ''}が含まれている状態が確認されました。`,
            why: paragraphs('これ自体で直ちに問題が起きるものではありません。', 'ただし、バージョンが分かると、そのバージョンに知られている弱点を狙った攻撃の下調べに使われやすくなります。'),
            action: '対策として、応答ヘッダーからバージョン情報を出さない設定にし、あわせてソフトウェアを最新の状態に保つことを推奨いたします。',
            pitch: '今回のようなバージョン情報の公開についても、単なる設定の指摘にとどまらず、実際にそのバージョンで悪用可能な弱点が残っているかまで確認することが可能です。',
        }),
    },
];
/**
 * 所見を平易な説明に組み立てる。分類できない所見は、題名・補足・推奨対応から一般的な形で組む。
 */
export const explainFinding = (finding, ctx = {}) => {
    const haystack = `${finding.category ?? ''} ${finding.title}`;
    const rule = EXPLANATION_RULES.find((r) => r.pattern.test(haystack));
    if (rule)
        return rule.build(finding, ctx);
    return {
        heading: finding.title,
        observed: `貴社Webサイトについて、次の事項が確認されました。${finding.title}${finding.evidence ? `（確認内容：${finding.evidence}）` : ''}`,
        why: paragraphs(finding.note?.trim() || 'そのままにしておくと、公開されている情報をもとに攻撃の足がかりにされる可能性があります。'),
        action: `対策として、${recommendationFor(finding).replace(/^対策として、?/, '')}`,
        pitch: PITCH_GENERIC,
    };
};
/**
 * 文面の「■ 確認された事項」に入れる本文。見出しの次の行に確認事項を置き、以降は空行で段落を分ける
 * （メールの HTML 化では空行が段落、1 つの改行が行内の改行になる）。
 */
export const renderFindingExplanation = (explanation) => [`【${explanation.heading}】\n${explanation.observed}`, ...explanation.why, explanation.action].join('\n\n');
//# sourceMappingURL=security.js.map